import React, { useState, useEffect, useContext } from 'react'
import { useParams, Link } from 'react-router-dom'
import { graphql } from '@apollo/client/react/hoc'
import gql from "graphql-tag"
import { Container, Row, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import './TaskManagementContainer.css'
import TaskManagementListHeader from './TaskManagementListHeader'
import TaskList from './TaskList'
import TaskKanbanView from './TaskKanbanView'
import { AllBoardsQuery } from './Queries'
import withApolloClient from '../withApolloClient'
import { CurrentUserContext } from '../Context/CurrentUserContext'
import BoardActionsMenu from './TaskComponents/BoardActionsMenu'


const UpdateBoardMutation = gql`
    mutation UpdateBoardMutation($input: UpdateBoardMutationInput!) {
        updateBoard(input: $input) {
            ok
            errors {
                field
                messages
            }
        }
    }
`

const UpdateMemberMutation = gql`
    mutation updateMember($input: UpdateMemberMutationInput!) {
        updateMember(input: $input) {
            ok
            errors {
                messages
                field
            }
        }
    }
`

function TaskManagementContainer({ allBoards, loadMoreEntries, apolloClient }) {
    const { boardId } = useParams()
    const { currentUser } = useContext(CurrentUserContext)
    const [orderBy, setOrderBy] = useState(['dueDate'])
    const [editedDescription, setEditedDescription] = useState("")
    const [descriptionOpen, setDescriptionOpen] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [groupBy, setGroupBy] = useState('status')
    const [viewType, setViewType] = useState(currentUser?.member?.taskManagementViewType || "list")
    useEffect(() => {
        if (currentUser?.member?.taskManagementViewType) {
            setViewType(currentUser.member.taskManagementViewType)
        }
    }, [currentUser?.member?.taskManagementViewType])

    useEffect(() => {
        if (!currentUser?.member?.kanbanColumnConfig) return
        try {
            const config = JSON.parse(currentUser.member.kanbanColumnConfig)
            setGroupBy(boardId && config[boardId] ? config[boardId] : 'status')
        } catch (e) { }
    }, [currentUser?.member?.kanbanColumnConfig, boardId])

    const handleGroupByChange = async (newGroupBy) => {
        setGroupBy(newGroupBy)
        if (!boardId) return
        try {
            let existing = {}
            if (currentUser?.member?.kanbanColumnConfig) {
                try { existing = JSON.parse(currentUser.member.kanbanColumnConfig) } catch (e) { }
            }
            await apolloClient.mutate({
                mutation: UpdateMemberMutation,
                variables: { input: { memberId: currentUser?.member?.id, kanbanColumnConfig: JSON.stringify({ ...existing, [boardId]: newGroupBy }) } },
                refetchQueries: ['UserQuery'],
            })
        } catch (e) { }
    }

    const handleViewTypeChange = async (newViewType) => {
        setViewType(newViewType)
        try {
            await apolloClient.mutate({
                mutation: UpdateMemberMutation,
                variables: {
                    input: {
                        memberId: currentUser?.member?.id,
                        taskManagementViewType: newViewType,
                    }
                },
                refetchQueries: ["UserQuery"],
            })
        } catch (error) {
            console.error("Error saving view type:", error)
        }
    }


    const [editingBoardId, setEditingBoardId] = useState(null)
    const [editBoardTitle, setEditBoardTitle] = useState("")
    const [hoveredBoardId, setHoveredBoardId] = useState(null)

    const [filterValues, setFilterValues] = useState({ searchTerm: '', isArchived: false })

    useEffect(() => {
        if (!boardId)
            setFilterValues({ ...filterValues, statuses: ["OPEN", "IN_PROGRESS"] })
    }, [boardId])

    const updateOrderBy = (string) => {
        let order = [...orderBy]
        if (!order.includes(string) && !order.includes("-" + string)) {
            order = [string]
        } else {
            let index = order.indexOf(string)
            if (order.indexOf(string) > -1) {
                order[index] = "-" + string
            } else {
                index = order.indexOf("-" + string)
                index > -1 && (order[index] = string)
            }
        }
        setOrderBy(order)
    }
    const handleSaveTaskField = async (field, value) => {
        if (!field) return
        setIsSaving(true)

        const input = {
            boardId,
            [field]: value,
        }

        try {
            await apolloClient.mutate({
                mutation: UpdateBoardMutation,
                variables: { input },
                refetchQueries: ["Board"],
            })

            if (field === "description") setDescriptionOpen(false)
        } catch (error) {
            console.error(`Error saving ${field}:`, error)
        }
        finally {
            setIsSaving(false)
        }
    }

    return (
        <div className="TaskManagementContainer">
            <Container fluid>
                <TaskManagementListHeader activeTab={!boardId || boardId === 'tasks' ? "myTasks" : "boardTasks"} filterValues={filterValues} setFilterValues={(res) => setFilterValues({ ...res })} showNewBoardButton viewType={viewType} onViewTypeChange={handleViewTypeChange} groupBy={groupBy} onGroupByChange={handleGroupByChange} />
                <Row>
                    <Col xs="2" className="TaskBoardColumn">
                        <ul className="TaskBoardList">
                            <li className={!boardId || boardId === 'tasks' ? 'active' : ''}>
                                <Link to="/boards/tasks/">My Tasks</Link>
                            </li>
                            {allBoards?.edges?.map((board, idx) => (
                                <li className={boardId === board.node.id ? 'active' : ''} key={idx} onMouseEnter={() => setHoveredBoardId(board.node.id)} onMouseLeave={() => setHoveredBoardId(null)}>
                                    <div className="board-sidebar-item">
                                        {editingBoardId === board.node.id ? (
                                            <input
                                                className="board-title-edit-input"
                                                value={editBoardTitle}
                                                autoFocus
                                                onChange={(e) => setEditBoardTitle(e.target.value)}
                                                onKeyDown={async (e) => {
                                                    if (e.key === 'Enter') {
                                                        await apolloClient.mutate({ mutation: UpdateBoardMutation, variables: { input: { boardId: board.node.id, title: editBoardTitle } }, refetchQueries: ['AllBoards'] })
                                                        setEditingBoardId(null)
                                                    } else if (e.key === 'Escape') {
                                                        setEditingBoardId(null)
                                                    }
                                                }}
                                                onBlur={async () => {
                                                    await apolloClient.mutate({ mutation: UpdateBoardMutation, variables: { input: { boardId: board.node.id, title: editBoardTitle } }, refetchQueries: ['AllBoards'] })
                                                    setEditingBoardId(null)
                                                }}
                                            />
                                        ) : (
                                            <Link to={`/boards/${board.node.id}/tasks/`}>{board.node.title}</Link>
                                        )}
                                        {editingBoardId !== board.node.id && (
                                            <>
                                                <span
                                                    className="board-edit-icon"
                                                    title="Rename"
                                                    onClick={() => { setEditBoardTitle(board.node.title); setEditingBoardId(board.node.id) }}
                                                >
                                                    <i className="fa fa-pencil" />
                                                </span>
                                                {boardId === board.node.id && (
                                                    <span
                                                        className="board-edit-icon"
                                                        title="Description"
                                                        onClick={() => { setEditedDescription(board.node.description || ""); setDescriptionOpen(true) }}
                                                    >
                                                        <i className="fa fa-align-left" />
                                                    </span>
                                                )}
                                                <span className="board-edit-icon board-actions-wrapper" onClick={(e) => e.stopPropagation()}>
                                                    <BoardActionsMenu boardId={board.node.id} />
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </li>
                            ))}
                            {allBoards?.pageInfo?.hasNextPage && (
                                <li>
                                    <Button outline color="secondary" onClick={loadMoreEntries}>
                                        Load more...
                                    </Button>
                                </li>
                            )}
                        </ul>
                    </Col>
                    <Col xs="10" className="TaskListColumn">
                        <Modal isOpen={descriptionOpen} toggle={() => setDescriptionOpen(false)}>
                            <ModalHeader toggle={() => setDescriptionOpen(false)}>
                                {allBoards?.edges?.find(b => b.node.id === boardId)?.node?.title} — Description
                            </ModalHeader>
                            <ModalBody>
                                <textarea
                                    className="form-control"
                                    style={{ minHeight: "120px" }}
                                    value={editedDescription}
                                    autoFocus
                                    onChange={(e) => setEditedDescription(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) handleSaveTaskField("description", editedDescription) }}
                                />
                            </ModalBody>
                            <ModalFooter>
                                <Button color="primary" onClick={() => handleSaveTaskField("description", editedDescription)} disabled={isSaving}>
                                    {isSaving ? <i className="fa fa-spinner fa-spin" /> : "Save"}
                                </Button>
                                <Button color="secondary" onClick={() => setDescriptionOpen(false)} disabled={isSaving}>Cancel</Button>
                            </ModalFooter>
                        </Modal>
                        {viewType === "kanban" ? (
                            boardId ?
                                <TaskKanbanView boardId={boardId} {...filterValues} orderBy={orderBy} groupBy={groupBy} /> :
                                <TaskKanbanView assignedToIds={[currentUser.id]} {...filterValues} orderBy={orderBy} groupBy={groupBy} />
                        ) : (
                            boardId ?
                                <TaskList boardId={boardId} {...filterValues} orderBy={orderBy} updateOrderBy={updateOrderBy} /> :
                                <TaskList assignedToIds={[currentUser.id]} {...filterValues} orderBy={orderBy} updateOrderBy={updateOrderBy} />
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default graphql(AllBoardsQuery, {
    options: ({ searchTerm }) => ({
        variables: {
            searchTerm,
            orderBy: ["date_added"],
            isArchived: false,
            first: 10
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network'
    }),
    props: ({ data: { loading, allBoards, fetchMore, searchTerm, variables, refetch } }) => (
        {
            loading,
            allBoards,
            variables,
            refetch,
            refetchDriversQuery: () => {
                //No apollo function that refetches in place with pagination considered so this function instead
                let currentLength = allBoards && allBoards.edges ? allBoards.edges.length : 1
                return fetchMore({
                    query: AllBoardsQuery,
                    variables: {
                        ...variables,
                        first: currentLength,
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        const newEdges = fetchMoreResult.allBoards.edges
                        const pageInfo = fetchMoreResult.allBoards.pageInfo
                        return {
                            allBoards: {
                                edges: [...newEdges],
                                pageInfo,
                                searchTerm: previousResult.searchTerm,
                                __typename: previousResult.allBoards.__typename
                            },
                        }
                    },
                })
            },
            loadMoreEntries: () => {
                return fetchMore({
                    query: AllBoardsQuery,
                    variables: {
                        cursor: allBoards.pageInfo.endCursor,
                        ...variables
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        const newEdges = fetchMoreResult.allBoards.edges
                        const pageInfo = fetchMoreResult.allBoards.pageInfo

                        return {
                            // Put the new comments at the end of the list and update `pageInfo`
                            // so we have the new `endCursor` and `hasNextPage` values
                            allBoards: {
                                edges: [...previousResult.allBoards.edges, ...newEdges],
                                pageInfo,
                                searchTerm: previousResult.searchTerm,
                                __typename: previousResult.allBoards.__typename
                            },
                        }
                    },
                })
            },
        }
    )
})(withApolloClient(TaskManagementContainer))